import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import Image from './image'

// interface BackgroundProps {
// 	backgroundImage: string
// 	backgroundPosition: 'top' | 'bottom' | 'left' | 'right' | 'center'
// 	backgroundStyle: 'static' | 'parallax'
// 	background_color: {
// 		type:
// 			| 'solid'
// 			| 'opacity-low'
// 			| 'opacity-high'
// 			| 'radial-gradient'
// 			| 'linear-gradient-left-to-right'
// 			| 'linear-gradient-right-to-left'
// 		color:
// 			| 'black'
// 			| 'white'
// 			| 'primary'
// 			| 'secondary'
// 			| 'accent'
// 			| 'none'
// 			| 'default'
// 	}
// }
const background_color_classes = {
	solid: {
		foreground: 'bg-foreground text-background',
		background: 'bg-background text-foreground',
		primary: 'bg-primary text-primary-foreground',
		secondary: 'bg-secondary text-secondary-foreground',
		accent: 'bg-accent text-accent-foreground',
		none: 'bg-none',
		default: 'bg-none',
	},
	'opacity-low': {
		foreground: 'bg-foreground/80',
		background: 'bg-background/80',
		primary: 'bg-primary/80',
		secondary: 'bg-secondary/80',
		accent: 'bg-accent/80',
		none: 'bg-none',
		default: 'bg-none',
	},
	'opacity-high': {
		foreground: 'bg-foreground/20',
		background: 'bg-background/20',
		primary: 'bg-primary/20',
		secondary: 'bg-secondary/20',
		accent: 'bg-accent/20',
		none: 'bg-none',
		default: 'bg-none',
	},
	'radial-gradient': {
		foreground: 'bg-[radial-gradient(foreground,_transparent)]',
		background:
			'bg-[radial-gradient(rgba(255,255,255,.9)_40%,_rgba(255,255,255,0.5))]',
		primary: 'bg-[radial-gradient(hsl(var(--primary)),_transparent)]',
		secondary: 'bg-[radial-gradient(hsl(var(--secondary)),_transparent)]',
		accent: 'bg-[radial-gradient(hsl(var(--accent)),_transparent)]',
		none: 'bg-none',
		default: 'bg-none',
	},
	'linear-gradient-left-to-right': {
		foreground: 'bg-gradient-to-r from-foreground',
		background: 'bg-gradient-to-r from-background',
		primary: 'bg-gradient-to-r from-primary',
		secondary: 'bg-gradient-to-r from-secondary',
		accent: 'bg-gradient-to-r from-accent',
		none: 'bg-none',
		default: 'bg-none',
	},
	'linear-gradient-right-to-left': {
		foreground: 'bg-gradient-to-l from-foreground',
		background: 'bg-gradient-to-l from-background',
		primary: 'bg-gradient-to-l from-primary',
		secondary: 'bg-gradient-to-l from-secondary',
		accent: 'bg-gradient-to-l from-accent',
		none: 'bg-none',
		default: 'bg-none',
	},
}
import { useMemo } from 'react'
import { DataBackground } from '@/types/components'

// export function useBackgroundColorClass(
// 	background: DataBackground['attributes'],
// ) {
// 	const background_color_type = background?.background_color_type ?? 'solid'
// 	const background_color = background?.background_color ?? 'default'

// 	return useMemo(() => {
// 		return background_color_classes[background_color_type][background_color]
// 	}, [background_color_type, background_color])
// }
export default function Background({
	content,
	children,
}: {
	content: DataBackground['attributes']
	children: React.ReactNode
}) {
	const background_image = content?.background_image || null
	const background_image_position = content?.background_image_position || null
	const background_image_style: string = content?.background_image_style || null
	const background_color_type: string =
		content?.background_color_type || 'solid'
	const background_color: string = content?.background_color || 'background'

	const ref = useRef<HTMLDivElement>(null)
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ['start start', 'end start'],
	})

	const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])

	const Wrapper = background_image_style === 'parallax' ? motion.div : 'div'
	const wrapperProps =
		background_image_style === 'parallax' ? { style: { y } } : {}

	const objectPositionClasses = {
		top: 'object-top',
		bottom: 'object-bottom',
		left: 'object-left',
		right: 'object-right',
		center: 'object-center',
	}

	if (content === null) return null

	const objectPositionClass = objectPositionClasses[background_image_position]

	const bgColorClass =
		background_color_classes[background_color_type][background_color]
	return (
		<div className={`group relative ${bgColorClass}`}>
			{children}
			<Wrapper ref={ref} className={`absolute inset-0 -z-10`} {...wrapperProps}>
				{background_image !== null && (
					<Image
						image={background_image}
						className={`-z-10 h-full w-full object-cover ${objectPositionClass}`}
					/>
				)}
			</Wrapper>
		</div>
	)
}
